import {
	Box,
	Breadcrumb,
	Button,
	Center,
	Flex,
	Separator,
	Show,
	Spinner,
	Stack,
	Text,
} from '@welbe/design-system';
import { Auth } from 'aws-amplify';
import { GENERIC_CURP } from 'constants/genericNationalIdentity';
import { useEffect, useState } from 'react';
import { LuUser } from 'react-icons/lu';
import { useParams } from 'react-router-dom';
import { EmployeeProvider } from './Employee.context';
import { useEmployee } from './Employee.hooks';
import { getEmployee } from './Employee.services';
import type { EmployeeProps } from './Employee.types';
import { AccessData } from './sections/AccessData/AccessData';
import { Company } from './sections/Company/Company';
import { ContactData } from './sections/ContactData/ContactData';
import { InheritanceRights } from './sections/InheritanceRights/InheritanceRights';
import { Insurance } from './sections/Insurance/Insurance';
import { JobProfile } from './sections/JobProfile/JobProfile';
import { PersonalData } from './sections/PersonalData/PersonalData';
import { Residence } from './sections/Residence/Residence';

export const Employee = () => {
	return (
		<EmployeeProvider>
			<EmployeeFormContent />
		</EmployeeProvider>
	);
};

const EmployeeFormContent = () => {
	const [isEdition, setEdition] = useState(false);
	const [employeeLoading, setEmployeeLoading] = useState(false);
	const [isPatientDataLocked, setPatientDataLocked] = useState(false);
	const {
		errors,
		fieldErrors,
		formLoading,
		onCancel,
		setEmployeeForm,
		onSubmit,
	} = useEmployee();
	const { id }: { id?: string } = useParams();

	const handlePermissions = async (isPatientDataLocked: boolean) => {
		const { idToken }: any = await Auth.currentSession();
		const permissions = idToken?.payload?.permissions
			? JSON.parse(idToken.payload.permissions)
			: [];

		if (permissions.includes('company.employees.profile-edition')) {
			setPatientDataLocked(false);
			return;
		}

		setPatientDataLocked(isPatientDataLocked);
	};

	const fetchEmployee = async (id: string) => {
		try {
			setEmployeeLoading(true);
			const { data }: { data: { content: EmployeeProps } } = await getEmployee(
				id,
			);
			handlePermissions(data?.content?.isPatientDataLocked || false);
			setEmployeeForm({
				...data.content,
				nationalIdentity: data?.content.hasGenericNationalIdentity
					? GENERIC_CURP
					: data.content.nationalIdentity || '',
				riskProfiles: data.content.riskProfiles?.map((item: any) => item.id),
				expositionAreas: data.content.expositionAreas?.map(
					(item: any) => item.id,
				),
			});
		} catch (error) {
			console.error('Error fetching employee:', error);
		} finally {
			setEmployeeLoading(false);
		}
	};

	useEffect(() => {
		if (id) {
			setEdition(true);
			fetchEmployee(id);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Box p="0" paddingInline="0" marginInline="0" w="100%">
			<Flex padding="1rem">
				<Breadcrumb.Root size="lg">
					<Breadcrumb.List listStyle="none">
						<Breadcrumb.Item
							cursor="pointer"
							fontSize="xl"
							fontWeight="500"
							gap="1rem"
							alignItems="center"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="24"
								height="21"
								viewBox="0 0 47 44"
								fill="none"
							>
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M23.375 0C20.1924 0 17.1401 1.26428 14.8897 3.51472C13.177 5.2274 12.0355 7.40449 11.5877 9.75H2.25C1.00736 9.75 0 10.7574 0 12V41.25C0 42.4926 1.00736 43.5 2.25 43.5H44.5C45.7426 43.5 46.75 42.4926 46.75 41.25V12C46.75 10.7574 45.7426 9.75 44.5 9.75H35.1622C34.7144 7.40449 33.5729 5.2274 31.8603 3.51472C29.6098 1.26428 26.5576 0 23.375 0ZM30.5295 9.75C30.1705 8.60845 29.5401 7.55851 28.6783 6.6967C27.2718 5.29018 25.3641 4.5 23.375 4.5C21.3858 4.5 19.4782 5.29018 18.0717 6.6967C17.2099 7.55851 16.5794 8.60845 16.2204 9.75H30.5295ZM42.25 14.25V22.75H25.5V21.75C25.5 20.5074 24.4926 19.5 23.25 19.5C22.0074 19.5 21 20.5074 21 21.75V22.75H4.5V14.25H42.25ZM4.5 27.25H21V28.25C21 29.4926 22.0074 30.5 23.25 30.5C24.4926 30.5 25.5 29.4926 25.5 28.25V27.25H42.25V39H4.5V27.25Z"
									fill="#64778F"
								/>
							</svg>
							<Breadcrumb.Link onClick={() => window.history.back()}>
								Colaboradores
							</Breadcrumb.Link>
						</Breadcrumb.Item>
						<Breadcrumb.Separator />
						<Breadcrumb.Item cursor="pointer">
							<Breadcrumb.Link href="#" fontSize="xl" fontWeight="500">
								{isEdition ? 'Editar colaborador' : 'Agregar colaborador'}
							</Breadcrumb.Link>
						</Breadcrumb.Item>
					</Breadcrumb.List>
				</Breadcrumb.Root>
			</Flex>
			<Box bg="white" padding={{ sm: '1rem', md: '2rem' }} w="100%" my="1rem">
				<Stack position="relative">
					<Box
						w="60px"
						h="60px"
						textAlign="center"
						bg="blue.500"
						display="flex"
						justifyContent="center"
						alignItems="center"
						borderRadius="full"
						position="absolute"
						top="-60px"
						left="50%"
						transform="translateX(-50%)"
					>
						<LuUser color="white" size={32} />
					</Box>
					<Text
						color="blue.500"
						fontWeight="600"
						fontSize="xl"
						textAlign="center"
						mt="1rem"
					>
						{isEdition ? 'Editar colaborador' : 'Agregar colaborador'}
					</Text>
				</Stack>
				<Show
					when={!employeeLoading}
					fallback={
						<Flex justify="center" align="center" h="50vh">
							<Spinner />
						</Flex>
					}
				>
					<Insurance
						disabled={isPatientDataLocked}
						error={errors?.includes('insuranceId')}
					/>
					<Separator w="100%" my="2rem" />
					<PersonalData
						disabled={isPatientDataLocked}
						errors={errors}
						fieldErrors={fieldErrors}
					/>
					<Separator w="100%" my="2rem" />
					<ContactData disabled={isPatientDataLocked} errors={errors} />
					<Separator w="100%" my="2rem" />
					<AccessData disabled={isPatientDataLocked} />
					<Separator w="100%" my="2rem" />
					<InheritanceRights disabled={isPatientDataLocked} errors={errors} />
					<Separator w="100%" my="2rem" />
					<Show
						when={!employeeLoading}
						fallback={
							<Center>
								<Text>Cargando datos de residencia...</Text>
							</Center>
						}
					>
						<Residence
							disabled={isPatientDataLocked}
							errors={errors}
							isEdition={isEdition}
						/>
					</Show>
					<Separator w="100%" my="2rem" />
					<Company disabled={isPatientDataLocked} errors={errors} />
					<Separator w="100%" my="2rem" />
					<JobProfile disabled={isPatientDataLocked} />
				</Show>
			</Box>
			<Flex
				gap="1rem"
				justify="flex-end"
				w="100%"
				padding={{ sm: '1rem', md: '2rem' }}
			>
				<Button
					variant="outline"
					minW="250px"
					onClick={onCancel}
					disabled={formLoading || employeeLoading}
				>
					Cancelar
				</Button>
				<Button
					minW="250px"
					onClick={() => !isPatientDataLocked && onSubmit()}
					disabled={formLoading || isPatientDataLocked}
					loading={formLoading || employeeLoading}
				>
					Continuar
				</Button>
			</Flex>
		</Box>
	);
};
